// Pure cold-boot phase math for the MATM __matmBootMark collector (MATM-1271).
//
// Kept dependency-free (no browser globals, no app imports) so it's
// deterministically unit-testable. bootMarkCollector.js reads the live
// Navigation Timing / performance API and delegates the arithmetic here.

// First mark with the given name, or undefined.
export function findMark(marks, name) {
	return marks.find((m) => m.name === name);
}

// `now` (ms since timeOrigin) of the first mark with `name`, or null.
export function markNow(marks, name) {
	const mark = findMark(marks, name);
	return mark ? mark.now : null;
}

// b - a, rounded — but null if either bound is missing (a missing mark yields a
// null phase rather than a misleading number).
export function span(a, b) {
	if (a == null || b == null) return null;
	const d = b - a;
	return Number.isFinite(d) ? Math.round(d) : null;
}

/**
 * Map the raw marks + Navigation Timing context into the five cold-boot phases
 * the RN-migration audit asked for (rec #3):
 *   webview-init / bundle-parse / lazy-chunk-load / auth-RTs / first-data-render.
 *
 * Each phase is bounded by two marks; a missing bound -> null (omitted from
 * analysis) rather than a fabricated value.
 *
 * @param {Array<{name:string, now:number}>} marks - buffered boot marks.
 * @param {object} nav - Navigation Timing context (all optional / nullable):
 *   @param {number|null} nav.domInteractive     - ms since timeOrigin.
 *   @param {number|null} nav.ttfbMs             - responseStart - requestStart.
 *   @param {number|null} nav.domContentLoadedMs - domContentLoadedEventEnd.
 * @param {number|null} terminalNow - `now` of the terminal (first-data) mark.
 */
export function computeBootPhases(marks, nav, terminalNow) {
	const domInteractive = nav?.domInteractive ?? null;

	const shimStart =
		markNow(marks, 'shim_boot_local_app_start') ??
		markNow(marks, 'shim_preload_start');
	const appRender = markNow(marks, 'matm_app_render');
	const authPending = markNow(marks, 'matm_app_auth_pending');
	const authOk = markNow(marks, 'matm_app_auth_ok');

	// webview-init: navigation start (0) -> document interactive. Proxy for
	// WebView spin-up + HTML/first-script arrival.
	const webviewInit =
		domInteractive != null ? Math.round(domInteractive) : null;

	// bundle-parse: interactive -> shim boot begins (main bundle parsed+executed
	// to the point the boot shim starts running).
	const bundleParse = span(domInteractive, shimStart);

	// lazy-chunk-load: shim boot -> MATM app first render (dynamic import + mount
	// of the app chunk & its lazy children).
	const lazyChunk = span(shimStart, appRender);

	// auth-RTs: the auth round-trips (pending -> ok). If auth resolved
	// synchronously, no pending mark fired -> 0 rather than null.
	const authRts = authPending != null ? span(authPending, authOk) : 0;

	// first-data-render: authorized -> first hero-path data painted (events feed
	// data, or chat thread mounted).
	const firstDataRender = span(authOk, terminalNow);

	return {
		webview_init_ms: webviewInit,
		bundle_parse_ms: bundleParse,
		lazy_chunk_load_ms: lazyChunk,
		auth_rt_ms: authRts,
		first_data_render_ms: firstDataRender,
		// Full cold boot: navigation start -> terminal mark.
		total_ms: terminalNow != null ? Math.round(terminalNow) : null,
		// Extra Navigation Timing context (cheap, already measured by the browser).
		ttfb_ms: nav?.ttfbMs ?? null,
		dom_content_loaded_ms:
			nav?.domContentLoadedMs != null
				? Math.round(nav.domContentLoadedMs)
				: null,
	};
}
